import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { combineLatest, forkJoin, Observable, of, throwError } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  Check, CheckCheckStageAggregate, CheckDTO, CheckStageDTO, CheckStatus,
  CheckCreateRequest, CheckUpdateRequest, CheckStatusUpdateRequest, CheckStageUpdateRequest,
  ReworkStatus
} from '../models/checks-model';
import { CommentDTO, CommentCreateDTO } from '../models/comment-model';
import { DocumentModel, DocumentCheckAggregate } from '../models/document-model';
import { User } from '../models/user-model';
import { Project } from '../models/project-model';
import { API_ENDPOINTS } from '../common/api/api-endpoints';
import { AvatarService } from '../users/avatar';

/**
 * CheckService
 *
 * Verantwortlichkeiten:
 * - Laden von Checks vom Backend per Stage-Aggregation
 *   (GET /project/{code}/document → eindeutige Checks → forkJoin Stages)
 * - CRUD-Operationen für Checks (via HTTP, spec-aligned)
 * - Mapping CheckDTO + CheckStageDTO[] → Check Domain-Modell
 * - Puffer für neu erstellte Checks (newlyCreatedChecks)
 * - Bereitstellung reaktiver Signals via createChecksSignal()
 */
@Injectable({
  providedIn: 'root',
})
export class CheckService {
  private readonly http = inject(HttpClient);
  private readonly avatarService = inject(AvatarService);

  /** Lokal gepufferte, neu erstellte Checks (noch kein Dokument → nicht in Projektliste) */
  private readonly _newlyCreatedChecks = signal<Check[]>([]);

  // ===== Signal Factory =====

  /**
   * Erstellt ein reaktives Signal für die Check-Liste.
   *
   * Rückgabewert:
   *   null      → Lade-Zustand (HTTP-Request läuft)
   *   Check[]   → Ergebnis (kann leer sein)
   *
   * @param usersByOid  Map der bekannten User nach OID
   * @param projectsById Map der Projekte nach contractNr
   * @param projectCode Optional: lädt Checks per Projekt-Dokumente (spec-aligned).
   *                    Ohne projectCode: Legacy-Mock-Fallback via GET /api/checks.
   */
  createChecksSignal(
    usersByOid: () => Map<string, User>,
    projectsById: () => Map<string, Project>,
    projectCode?: () => string | null
  ): Signal<Check[] | null> {
    if (projectCode) {
      // Reaktiv auf BEIDE Signals: projectCode UND projectsById.
      // Verhindert Race Condition: projectCode kommt synchron aus Route-Params,
      // projectsById wird erst nach dem API-Call für das Projekt befüllt.
      const loadedChecks = toSignal(
        combineLatest([
          toObservable(computed(projectCode)),
          toObservable(computed<Map<string, Project>>(projectsById)),
        ]).pipe(
          switchMap(([code, projects]) => {
            if (!code) return of([] as Check[] | null);
            // Projekt noch nicht geladen → Lade-Zustand signalisieren
            if (projects.size === 0) return of(null as Check[] | null);
            // Projekt verfügbar → laden; null als Ladeindikator voranstellen
            return this.loadChecksWithStagesByProject(code, usersByOid(), projects).pipe(
              startWith(null as Check[] | null)
            );
          })
        ),
        { initialValue: null as Check[] | null }
      );

      return computed<Check[] | null>(() => {
        const loaded = loadedChecks();
        if (loaded === null) return null; // weiterhin laden
        const newOnes = this._newlyCreatedChecks();
        const loadedIds = new Set(loaded.map(c => c.id));
        // Neu erstellte Checks oben einsortieren (werden nach Reload dedupliziert)
        return [...newOnes.filter(c => !loadedIds.has(c.id)), ...loaded];
      });
    }

    // Legacy-Fallback (kein projectCode übergeben) – lädt alle Checks ungefiltert
    const checksDto = toSignal(
      this.http.get<CheckDTO[]>(API_ENDPOINTS.CHECK),
      { initialValue: [] as CheckDTO[] }
    );
    return computed<Check[]>(() => {
      const users = usersByOid();
      const projects = projectsById();
      const newOnes = this._newlyCreatedChecks();
      const mapped = checksDto().map(dto => this.mapCheck(dto, users, projects));
      const mappedIds = new Set(mapped.map(c => c.id));
      return [...newOnes.filter(c => !mappedIds.has(c.id)), ...mapped];
    });
  }

  createChecksByIdSignal(checks: () => Check[]) {
    return computed(() => new Map(checks().map(c => [c.id, c])));
  }

  createFilteredChecksSignal(checks: () => Check[], projectCode: () => string | null) {
    return computed(() => {
      const code = projectCode();
      if (!code) return [];
      return checks().filter(c => c.project?.contractNr === code);
    });
  }

  // ===== Stage-Aggregation =====

  /**
   * Lädt alle Checks eines Projekts über die Dokumentenliste.
   *
   * Ablauf:
   *   GET /project/{code}/document     → DocumentCheckAggregate[]  (Dokumente, checks=[])
   *   GET /document/{docId}/check      → CheckCheckStageAggregate[] (Checks+Stages, pro Dokument)
   *   → Checks über Dokumente hinweg deduplizieren
   *   → mapCheckWithStages() (Stages bereits eingebettet, kein Extra-Call)
   */
  loadChecksWithStagesByProject(
    projectCode: string,
    usersByOid: Map<string, User>,
    projectsById: Map<string, Project>
  ): Observable<Check[]> {
    const project = projectsById.get(projectCode) ?? null;
    if (!project) return of([]);

    return this.http.get<DocumentCheckAggregate[]>(`${API_ENDPOINTS.PROJECT}/${projectCode}/document`).pipe(
      switchMap(docs => {
        if (docs.length === 0) return of([] as Check[]);
        return forkJoin(docs.map(doc => this.getChecksByDocumentApi(doc.guid))).pipe(
          map(checksPerDoc => {
            // Checks mehrerer Dokumente flach zusammenführen und deduplizieren
            const seen = new Set<string>();
            return checksPerDoc.flat().filter(agg => {
              if (seen.has(agg.guid)) return false;
              seen.add(agg.guid);
              return true;
            });
          }),
          map(unique =>
            unique.map(agg =>
              this.mapCheckWithStages(
                { guid: agg.guid, createdAt: agg.createdAt, description: agg.description, doneAt: agg.doneAt },
                agg.stages,
                project,
                usersByOid
              )
            )
          )
        );
      })
    );
  }

  /**
   * Lädt einen einzelnen Check per ID inkl. seiner Stages und zugewiesener User.
   *
   * Ablauf:
   *   1. GET /check/{id}          → CheckDTO
   *   2. GET /check/{id}/stage    → CheckStageDTO[] (mit assigneeId)
   *   3. GET /user/{oid}          → Avatar je einzigartiger assigneeId (parallel)
   *   → usersByOid-Map bauen → mapCheckWithStages()
   *
   * Gibt null zurück, wenn das Projekt anhand des projectCode nicht aufgelöst werden kann
   * (z.B. weil projectsById noch leer ist → Lade-Indikator).
   */
  loadCheckWithStages(
    checkId: string,
    projectCode: string,
    projectsById: Map<string, Project>
  ): Observable<Check | null> {
    return forkJoin({
      dto:    this.fetchCheckByIdApi(checkId),
      stages: this.fetchCheckStagesApi(checkId),
    }).pipe(
      switchMap(({ dto, stages }) => {
        const uniqueIds = [
          ...new Set(stages.map(s => s.assigneeId).filter((id): id is string => !!id))
        ];
        const users$ = uniqueIds.length > 0
          ? forkJoin(uniqueIds.map(id => this.avatarService.getAvatarByOid(id)))
          : of([] as User[]);
        return users$.pipe(map(users => ({ dto, stages, users })));
      }),
      map(({ dto, stages, users }) => {
        const project = projectsById.get(projectCode) ?? null;
        if (!project) return null;
        const usersByOid = new Map<string, User>(users.map(u => [u.oid, u]));
        return this.mapCheckWithStages(dto, stages, project, usersByOid);
      })
    );
  }

  /**
   * Fügt einen frisch erstellten Check in den lokalen Puffer ein.
   * Er erscheint sofort oben in der Liste (Pinning), bis die Backend-Liste
   * ihn nach dem nächsten Laden enthält.
   */
  addCreatedCheck(dto: CheckDTO, stages: CheckStageDTO[], project: Project, usersByOid: Map<string, User> = new Map()): void {
    const check = this.mapCheckWithStages(dto, stages, project, usersByOid);
    this._newlyCreatedChecks.update(list => [check, ...list]);
  }

  // ===== Backend API Methoden =====

  /**
   * GET /check?projectCode={code} – alle Checks eines Projekts.
   * Wird von loadChecksWithStagesByProject() verwendet.
   */
  fetchChecksApi(projectCode?: string, status?: string): Observable<CheckDTO[]> {
    let params = new HttpParams();
    if (projectCode) params = params.set('projectCode', projectCode);
    if (status) params = params.set('status', status);
    return this.http.get<CheckDTO[]>(API_ENDPOINTS.CHECK, { params });
  }

  /** GET /check/{guid} */
  fetchCheckByIdApi(guid: string): Observable<CheckDTO> {
    return this.http.get<CheckDTO>(`${API_ENDPOINTS.CHECK}/${guid}`);
  }

  /** GET /check/{guid}/stage */
  fetchCheckStagesApi(guid: string): Observable<CheckStageDTO[]> {
    return this.http.get<CheckStageDTO[]>(`${API_ENDPOINTS.CHECK}/${guid}/stage`);
  }

  /** GET /document/{docId}/check – alle Checks eines Dokuments (inkl. eingebetteter Stages) */
  getChecksByDocumentApi(docId: string): Observable<CheckCheckStageAggregate[]> {
    return this.http.get<CheckCheckStageAggregate[]>(`${API_ENDPOINTS.DOCUMENT}/${docId}/check`);
  }

  /** GET /check/{guid}/document */
  getCheckDocumentsApi(guid: string): Observable<DocumentModel[]> {
    return this.http.get<DocumentModel[]>(`${API_ENDPOINTS.CHECK}/${guid}/document`);
  }

  /**
   * POST /check/{checkId}/document/{documentId} → 201
   * Verknüpft ein bestehendes Dokument mit einem Check.
   * (Kein Request-Body – documentId ist Pfad-Parameter.)
   */
  linkDocumentApi(checkGuid: string, documentId: string): Observable<DocumentCheckAggregate[]> {
    return this.http.post<DocumentCheckAggregate[]>(
      `${API_ENDPOINTS.CHECK}/${checkGuid}/document/${documentId}`,
      null
    );
  }

  /**
   * @todo Backend-Endpunkt noch nicht implementiert.
   * DELETE /check/{checkGuid}/document/{docGuid} ist nicht in der OpenAPI-Spec.
   */
  unlinkDocumentApi(_checkGuid: string, _docGuid: string): Observable<never> {
    return throwError(() => new Error('NOT_YET_IMPLEMENTED: Kein DELETE /check/{id}/document/{id} in der Spec'));
  }

  /** GET /check/{guid}/comment – alle Kommentare des Checks */
  getCheckCommentsApi(guid: string): Observable<CommentDTO[]> {
    return this.http.get<CommentDTO[]>(`${API_ENDPOINTS.CHECK}/${guid}/comment`);
  }

  /** GET /check/{checkId}/stage/{stageId}/comment */
  getCheckStageCommentsApi(checkGuid: string, stageId: number): Observable<CommentDTO[]> {
    return this.http.get<CommentDTO[]>(`${API_ENDPOINTS.CHECK}/${checkGuid}/stage/${stageId}/comment`);
  }

  /**
   * POST /check/{checkId}/stage/{stageId}/comment → 201
   * Kommentare werden immer einer konkreten Stage zugeordnet.
   */
  addCommentApi(checkGuid: string, stageId: number, payload: CommentCreateDTO): Observable<CommentDTO> {
    return this.http.post<CommentDTO>(
      `${API_ENDPOINTS.CHECK}/${checkGuid}/stage/${stageId}/comment`,
      payload
    );
  }

  /** POST /check → 201 */
  createCheckApi(payload: CheckCreateRequest): Observable<CheckDTO> {
    return this.http.post<CheckDTO>(API_ENDPOINTS.CHECK, payload);
  }

  /** PUT /check/{guid} → 202 */
  updateCheckApi(guid: string, payload: CheckUpdateRequest): Observable<CheckDTO> {
    return this.http.put<CheckDTO>(`${API_ENDPOINTS.CHECK}/${guid}`, payload);
  }

  /** PUT /check/{guid}/status → 202 */
  updateCheckStatusApi(guid: string, payload: CheckStatusUpdateRequest): Observable<CheckStageDTO[]> {
    return this.http.put<CheckStageDTO[]>(`${API_ENDPOINTS.CHECK}/${guid}/status`, payload);
  }

  /**
   * PUT /check/{checkId}/stage/{stageId} → 202
   * Aktualisiert den Status und/oder die Phase einer einzelnen Stage.
   * Entspricht dem Backend-Endpunkt UpdateCheckStage mit CheckStageStub-Body.
   */
  updateCheckStageApi(checkId: string, stageId: number, payload: CheckStageUpdateRequest): Observable<CheckStageDTO> {
    return this.http.put<CheckStageDTO>(`${API_ENDPOINTS.CHECK}/${checkId}/stage/${stageId}`, payload);
  }

  /**
   * @todo Backend-Endpunkt noch nicht implementiert.
   * DELETE /check/{guid} ist nicht in der OpenAPI-Spec.
   */
  deleteCheckApi(_guid: string): Observable<never> {
    return throwError(() => new Error('NOT_YET_IMPLEMENTED: Kein DELETE /check/{guid} in der Spec'));
  }

  // ===== Model-Mapping =====

  /**
   * Mappt CheckDTO + CheckStageDTO[] → Check Domain-Modell.
   * Stage-Phase-Mapping:
   *   FIRST  → creator + checker11 + dueDate1
   *   SECOND → checker12
   *   REVIEW → checker2 + dueDate2
   */
  mapCheckWithStages(
    dto: CheckDTO,
    stages: CheckStageDTO[],
    project: Project,
    usersByOid: Map<string, User> = new Map()
  ): Check {
    const first  = stages.find(s => s.phase === 'FIRST');
    const second = stages.find(s => s.phase === 'SECOND');
    const review = stages.find(s => s.phase === 'REVIEW');

    /**
     * Gibt einen User für eine Stage zurück.
     * Priorität: 1. usersByOid-Map  2. eingebettetes assignee-Objekt  3. Fallback mit roher OID
     */
    const stageUser = (stage: CheckStageDTO | undefined): User | null => {
      if (!stage) return null;
      // Neue API: assignee als eingebettetes Objekt
      const oid = stage.assignee?.oid ?? stage.assigneeId ?? null;
      if (!oid) return null;
      return usersByOid.get(oid) ?? {
        oid,
        displayName: stage.assignee?.displayName ?? oid,
        egeckoId: '', createdAt: '', lastAccessAt: '',
        roles: [], scopes: [], department: null,
      };
    };

    // Reihenfolge der Phasen – wird von deriveStatus() und deriveReworkStatus() gemeinsam genutzt.
    const phaseOrder: Array<{ stage: CheckStageDTO | undefined; status: CheckStatus }> = [
      { stage: first,  status: CheckStatus['Kontrolle 1-1'] },
      { stage: second, status: CheckStatus['Kontrolle 1-2'] },
      { stage: review, status: CheckStatus['Kontrolle 2']   },
    ];

    /**
     * Leitet den CheckStatus aus den Stage-Daten ab.
     * Die erste Stage in der Reihenfolge FIRST → SECOND → REVIEW, deren Status weder
     * 'DONE' noch 'CANCELLED' ist, bestimmt die aktuelle Phase.
     * Bei Gutachterlicher Tätigkeit wird K1-2 explizit als DONE geschlossen (sequenzieller
     * API-Call in confirmForwardCheck)
     * Sind alle relevanten Stages abgeschlossen → Abgeschlossen.
     */
    const deriveStatus = (): CheckStatus => {
      for (const entry of phaseOrder) {
        if (entry.stage && entry.stage.status !== 'DONE' && entry.stage.status !== 'CANCELLED') {
          return entry.status;
        }
      }
      return CheckStatus['Abgeschlossen'];
    };

    /**
     * Leitet den ReworkStatus aus den Stage-Daten ab.
     * Die aktive Stage (erste nicht-DONE Stage) bestimmt den Rework-Zustand:
     *   REWORK_REQUIRED  → Wiedervorlage
     *   CHECK_IMPOSSIBLE → PruefungNichtMoeglich
     *   CREATED / REVISED / CANCELLED / DONE → None
     *
     * Nach REVISED übernimmt der aktive Prüfer der Phase wieder – isInRework() wird false.
     */
    const deriveReworkStatus = (): ReworkStatus => {
      for (const entry of phaseOrder) {
        if (entry.stage && entry.stage.status !== 'DONE') {
          if (entry.stage.status === 'REWORK_REQUIRED')  return ReworkStatus.Wiedervorlage;
          if (entry.stage.status === 'CHECK_IMPOSSIBLE') return ReworkStatus.PruefungNichtMoeglich;
          return ReworkStatus.None; // CREATED, REVISED, CANCELLED
        }
      }
      return ReworkStatus.None; // Alle Stages DONE
    };

    return {
      id: dto.guid,
      displayName: dto.displayName ?? dto.description,
      description: dto.description,
      createdAt: dto.createdAt,
      doneAt: dto.doneAt ?? null,
      project,
      status: deriveStatus(),
      reworkStatus: deriveReworkStatus(),
      // FIRST-Stage-Assignee ist gleichzeitig creator und checker11
      creator:   stageUser(first),
      checker11: stageUser(first),
      checker12: stageUser(second),
      checker2:  stageUser(review),
      dueDate1:  first?.dueAt  ? new Date(first.dueAt)  : new Date(),
      dueDate12: second?.dueAt ? new Date(second.dueAt) : null,
      dueDate2:  review?.dueAt ? new Date(review.dueAt) : null,
      sharepointFolderPath: dto.sharepointFolderPath ?? '',
      directoryPFolderPath: dto.directoryPFolderPath ?? '',
      isConsultant: dto.isConsultant ?? false,
    };
  }

  /**
   * @deprecated Nur für den alten Mock-Pfad (kein Stage-Lookup, kein Status-Mapping aus Stages).
   * Für alle echten API-Pfade ist mapCheckWithStages() zu verwenden.
   * Kann entfernt werden, sobald der Legacy-Mock-Betrieb vollständig abgelöst ist.
   */
  mapCheck(
    dto: CheckDTO,
    usersByOid: Map<string, User>,
    projectsById: Map<string, Project>
  ): Check {
    return {
      id: dto.guid,
      displayName: dto.displayName ?? dto.description,
      description: dto.description,
      createdAt: dto.createdAt,
      doneAt: dto.doneAt,
      project: projectsById.get(dto.projectCode ?? '')!,
      status: dto.status ?? CheckStatus['Kontrolle 1-1'],
      reworkStatus: dto.reworkStatus ?? ReworkStatus.None,
      creator:   dto.creator   ? (usersByOid.get(dto.creator)   ?? null) : null,
      checker11: dto.checker11 ? (usersByOid.get(dto.checker11) ?? null) : null,
      checker12: dto.checker12 ? (usersByOid.get(dto.checker12) ?? null) : null,
      checker2:  dto.checker2  ? (usersByOid.get(dto.checker2)  ?? null) : null,
      dueDate1: dto.dueDate1 ? new Date(dto.dueDate1) : new Date(),
      dueDate12: dto.dueDate12 ? new Date(dto.dueDate12) : null,
      dueDate2: dto.dueDate2 ? new Date(dto.dueDate2) : null,
      sharepointFolderPath: dto.sharepointFolderPath ?? '',
      directoryPFolderPath: dto.directoryPFolderPath ?? '',
      isConsultant: dto.isConsultant ?? false,
    };
  }
}
